import { useMemo, useState } from 'react';
import { t } from '@/i18n';
import { PageLayout } from '@/components/layout/PageLayout';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { StatsKpis } from '@/components/stats/StatsKpis';
import { TrendChart } from '@/components/stats/TrendChart';
import { CarrierScorecard } from '@/components/stats/CarrierScorecard';
import { ExportCsvButton, kpiCsv, carriersCsv } from '@/components/stats/ExportCsvButton';
import { StatsAsk } from '@/components/stats/StatsAsk';
import { CarrierDetailSheet } from '@/components/stats/CarrierDetailSheet';
import { UrgencyStats } from '@/components/stats/UrgencyStats';
import { ArrivalHeatmap } from '@/components/stats/ArrivalHeatmap';
import { DwellTimes } from '@/components/stats/DwellTimes';
import { useSubView } from '@/components/layout/subViews';
import { carrierScorecard } from '@/data/carrierStats';
import { history, summarize, PILOT_START_INDEX } from '@/data/statsData';
import { urgencySummary } from '@/data/urgencyStats';
import { dwellSummary } from '@/data/siteStats';
import { dailySummary, weeklyReport } from '@/data/reportsData';
import { DailySummary } from '@/components/reports/DailySummary';
import { WeeklyReport } from '@/components/reports/WeeklyReport';
import { Info } from 'lucide-react';
import { HelpTip } from '@/components/ui/help-tip';

// KPIs always compare the last 30 days with the pre-pilot baseline,
// independent of the chart range.
const summary = summarize(history, 30);
// Reports cover fixed periods (yesterday, last week), not the chosen range.
const daily = dailySummary();
const weekly = weeklyReport();

// Chart colors from the design tokens in src/index.css.
const COLORS = {
  red: 'hsl(var(--destructive))',
  blue: 'hsl(var(--chart-1))',
  green: 'hsl(var(--chart-3))',
  amber: 'hsl(var(--chart-4))',
  slate: 'hsl(var(--chart-5))',
};

// Segmented period switch: navy for the active period, the orange accent stays for highlights.
const periodItem = 'text-xs h-7 px-3 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground';

export default function Statistiky() {
  // 90 days by default so the pilot start and the before/after change are visible.
  const [days, setDays] = useState('90');
  const [carrierId, setCarrierId] = useState(null);
  const view = useSubView('/statistiky');
  const scorecard = useMemo(() => carrierScorecard(Number(days)), [days]);
  const urgency = useMemo(() => urgencySummary(Number(days)), [days]);
  const dwell = useMemo(() => dwellSummary(Number(days)), [days]);
  const rows = useMemo(() => history.slice(-Number(days)).map(r => ({
    ...r,
    manual: r.urgencies - r.autoResolved,
  })), [days]);
  const pilotDate = history[PILOT_START_INDEX].date;
  const pilotVisible = rows.some(r => r.date === pilotDate);

  return (
    <PageLayout title={t('stats.title')}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-800">
            <span className="text-amber-500" aria-hidden="true">●</span> {t('stats.demoData')}
          </Badge>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Info className="h-3 w-3" /> {t('stats.demoHint')}
          </span>
          {view !== 'reports' && <div className="ml-auto flex items-center gap-1">
          <HelpTip id="period" />
          <ToggleGroup type="single" value={days} onValueChange={v => v && setDays(v)} className="gap-0.5 rounded-lg border border-border bg-card p-0.5">
            <ToggleGroupItem value="30" className={periodItem}>{t('stats.last30')}</ToggleGroupItem>
            <ToggleGroupItem value="90" className={periodItem}>{t('stats.last90')}</ToggleGroupItem>
          </ToggleGroup>
          </div>}
        </div>

        {/* Views are switched from the sidebar (desktop) or the chips (mobile). */}
        <div>
          {view === 'overview' && <div className="space-y-6">
            <StatsAsk />
            <div className="space-y-2" data-tour="stats-kpis">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-xs text-muted-foreground flex items-center gap-1">{t('stats.kpiPeriod')} <HelpTip id="kpis" /></div>
                <div className="ml-auto">
                  <ExportCsvButton name="kpi" days={days} build={locale => kpiCsv(rows, locale)} />
                </div>
              </div>
              <StatsKpis summary={summary} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TrendChart
                title={t('stats.chart.noShows')}
                hint={t('stats.chart.noShowsHint')}
                rows={rows}
                pilotDate={pilotVisible ? pilotDate : null}
                series={[{ key: 'noShows', name: t('stats.kpi.noShows'), color: COLORS.red, type: 'bar' }]}
              />
              <TrendChart
                title={t('stats.chart.informed')}
                hint={t('stats.chart.informedHint')}
                rows={rows}
                unit=" %"
                pilotDate={pilotVisible ? pilotDate : null}
                series={[{ key: 'informedPct', name: t('stats.kpi.informed'), color: COLORS.blue }]}
              />
              <TrendChart
                title={t('stats.chart.urgencies')}
                hint={t('stats.chart.urgenciesHint')}
                rows={rows}
                pilotDate={pilotVisible ? pilotDate : null}
                series={[
                  { key: 'autoResolved', name: t('stats.series.auto'), color: COLORS.green, type: 'bar', stack: 'u' },
                  { key: 'manual', name: t('stats.series.planner'), color: COLORS.amber, type: 'bar', stack: 'u' },
                ]}
              />
              <TrendChart
                title={t('stats.chart.parking')}
                hint={t('stats.chart.parkingHint')}
                rows={rows}
                pilotDate={pilotVisible ? pilotDate : null}
                series={[{ key: 'parkingTrucks', name: t('stats.kpi.parking'), color: COLORS.slate, type: 'bar' }]}
              />
            </div>
          </div>}

          {view === 'carriers' && <div className="space-y-2">
            <div className="flex justify-end">
              <ExportCsvButton name="dopravci" days={days} build={locale => carriersCsv(scorecard, locale)} />
            </div>
            <CarrierScorecard rows={scorecard} onSelect={setCarrierId} />
          </div>}

          {view === 'urgency' && <UrgencyStats summary={urgency} />}

          {view === 'site' && <div className="space-y-6">
            <ArrivalHeatmap days={Number(days)} />
            <DwellTimes rows={dwell} />
          </div>}

          {view === 'reports' && <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <DailySummary report={daily} />
            <WeeklyReport report={weekly} />
          </div>}
        </div>

        <CarrierDetailSheet carrierId={carrierId} onClose={() => setCarrierId(null)} />
      </div>
    </PageLayout>
  );
}
